// @ts-strict-ignore
import * as React from 'react';
import clsx from 'clsx';
import produce from 'immer';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { Placement } from 'common/components/tooltip/types';
import { useSegmentFilterController } from 'common/contexts/react/segment-filter-controller-context';
import { useTranslation } from 'common/i18n/hooks';
import type { Theme } from 'common/report/queries/app-api/themes';
import timing from 'common/stylesheets/mixins/timing.json';
import { insertAtIndex, removeByIndex, replaceByIndex } from 'common/util';
import { useThemes } from 'common/contexts/react/themes-context';
import { INLINE_THEME_NAME_KEY, MAX_THEME_COLORS, MIN_THEME_COLORS } from '../constants';
import { ColorThemeSwatch } from '../swatch';
import { launchSaveThemeModal, SaveThemeModalResult } from '../save-theme-modal';
import { generateInlineTheme, getColorsForMode } from '../utils';
import { ColorPicker } from '../color-picker';
import styles from './index.module.styl';
export function ColorThemeEditor(_ref: {
  theme: Theme;
  onSave: (theme: Theme) => void;
  onApply: (theme: Theme) => void;
  onCancel: () => void;
}) {
  let {
    theme,
    onSave,
    onApply,
    onCancel
  }: {
    theme: Theme;
    onSave: (theme: Theme) => void;
    onApply: (theme: Theme) => void;
    onCancel: () => void;
  } = _ref;
  const {
    t
  } = useTranslation();
  const themesResource = useThemes();
  const segmentFilterController = useSegmentFilterController();
  const [editVersion, setEditVersion] = React.useState(0);
  const [isEditingDarkMode, setIsEditingDarkMode] = React.useState(themesResource.isDarkMode());
  const [activeTheme, setActiveTheme] = React.useState(() => {
    if (theme.id) {
      return theme;
    }
    return generateInlineTheme(theme, t(INLINE_THEME_NAME_KEY), isEditingDarkMode ? "dark" : "light");
  });
  const [colorIndex, setColorIndex] = React.useState(0);

  // if colors aren't defined for the active light/dark mode, we only update the theme with these when updateThemeColors is called
  const activeColors = getColorsForMode(activeTheme, isEditingDarkMode);
  const activeColor = activeColors[colorIndex];
  const isCreatingTheme = !activeTheme.id;
  const isAddingDisabled = activeColors.length >= MAX_THEME_COLORS;
  const isDeletingDisabled = activeColors.length <= MIN_THEME_COLORS;

  /** makes sure to undo any page color scheme changes when this component is unmounted */
  React.useEffect(() => {
    const isDarkModeActive = themesResource.isDarkMode();
    segmentFilterController.setDraftTheme(activeTheme);
    return () => {
      themesResource.updateColorSchemeClass({
        darkMode: isDarkModeActive
      });
      segmentFilterController.setDraftTheme(null);
    };
  }, []);
  const updateThemeColors = function updateThemeColors(colors: string[]) {
    var _scheme;
    let updatedColorIndex: number = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    let scheme: `darkColors` | `colors` = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    scheme = (_scheme = scheme) !== null && _scheme !== void 0 ? _scheme : isEditingDarkMode ? "darkColors" : "colors";
    const updatedTheme = produce(activeTheme, draftTheme => {
      draftTheme.data[scheme] = colors;
    });
    setActiveTheme(updatedTheme);
    setEditVersion(editVersion + 1);
    setColorIndex(Math.max(0, updatedColorIndex !== null && updatedColorIndex !== void 0 ? updatedColorIndex : colorIndex));
    segmentFilterController.setDraftTheme(updatedTheme);
  };
  const onClickShare = (saveableTheme: Theme) => {
    launchSaveThemeModal({
      theme: saveableTheme
    }).then((_ref2: SaveThemeModalResult) => {
      let {
        updatedTheme
      }: SaveThemeModalResult = _ref2;
      onSave(updatedTheme);
    }).catch(() => {
      // Modal was closed
    });
  };
  const handleDarkModeToggle = () => {
    const darkMode = !isEditingDarkMode;
    setIsEditingDarkMode(darkMode);
    themesResource.updateColorSchemeClass({
      darkMode
    });
  };
  const applyDragDrop = (array: string[], dragIndex: number, dropIndex: number) => {
    return produce(array, draft => {
      const [removed] = draft.splice(dragIndex, 1);
      draft.splice(dropIndex, 0, removed);
    });
  };
  return <div className={styles.editor} data-sentry-component="ColorThemeEditor" data-sentry-source-file="index.tsx">
      <div className={styles.paneTitle}>{isCreatingTheme ? t("chart-customization.new-theme") : theme.name}</div>
      <div className={styles.line}>
        <ButtonReact className={styles.button} theme={ButtonTheme.Secondary} icon={isEditingDarkMode ? "light-mode" : "dark-mode"} tooltip={{
        text: isEditingDarkMode ? t("common.light-mode") : t("common.dark-mode")
      }} onClick={handleDarkModeToggle} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx" />
        <ColorThemeSwatch hideName theme={activeTheme} activeIndex={colorIndex} onColorClick={setColorIndex} darkMode={isEditingDarkMode} onColorDragDrop={(dragIndex, dropIndex) => {
        updateThemeColors(applyDragDrop(activeColors, dragIndex, dropIndex), dropIndex);
      }} data-sentry-element="ColorThemeSwatch" data-sentry-source-file="index.tsx" />
        <ButtonReact className={styles.button} theme={ButtonTheme.Secondary} icon='plus' disabled={isAddingDisabled} onClick={() => updateThemeColors(insertAtIndex(activeColors, colorIndex, activeColor), colorIndex + 1)} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx" />
      </div>
      <ColorPicker color={activeColor} onChange={newColor => {
      updateThemeColors(replaceByIndex(activeColors, colorIndex, newColor));
    }} onDelete={isDeletingDisabled ? undefined : () => updateThemeColors(removeByIndex(activeColors, colorIndex), colorIndex - 1)} data-sentry-element="ColorPicker" data-sentry-source-file="index.tsx" />
      <div className={styles.submitButtonsWrapper}>
        <ButtonReact className={clsx(styles.button, {
        [styles.nextLine]: isCreatingTheme
      })} theme={ButtonTheme.Secondary} onClick={() => onCancel()} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
          {t("common.cancel")}
        </ButtonReact>
        <ButtonReact className={styles.button} theme={ButtonTheme.Secondary} icon='save' onClick={() => onClickShare(activeTheme)} tooltip={{
        text: t("chart-customization.save-theme-tooltip"),
        placement: Placement.Bottom,
        'delay-in': timing["$mp-slow-tooltip-delay-in"]
      }} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
          {t("chart-customization.save-theme")}
        </ButtonReact>
        {isCreatingTheme && <ButtonReact className={styles.button} theme={ButtonTheme.Primary} onClick={() => onApply(generateInlineTheme(activeTheme, t(INLINE_THEME_NAME_KEY)))} tooltip={{
        text: t("chart-customization.apply-theme-tooltip"),
        placement: Placement.Bottom,
        'delay-in': timing["$mp-slow-tooltip-delay-in"]
      }}>
            {t("common.apply")}
          </ButtonReact>}
      </div>
    </div>;
}